import { createChatStream } from "../api/client";
import type { BlueprintUpdateEvent, ChatEvent, ConversationTurn, StageChangeEvent } from "../api/types";

export interface ChatPaneOptions {
  sessionId: string;
  onBlueprintUpdate: (event: BlueprintUpdateEvent) => void;
  onStageChange: (event: StageChangeEvent) => void;
  onError: (message: string) => void;
}

const SEND_ICON = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>`;

export class ChatPane {
  private container: HTMLElement;
  private opts: ChatPaneOptions;
  private messagesEl: HTMLElement;
  private inputEl: HTMLInputElement;
  private sendBtn: HTMLButtonElement;
  private chipsRowEl: HTMLElement;
  private selectedChips: Set<string> = new Set();
  private currentAiBubble: HTMLElement | null = null;
  private cancelStream: (() => void) | null = null;

  constructor(container: HTMLElement, opts: ChatPaneOptions) {
    this.container = container;
    this.opts = opts;
    this.container.innerHTML = this._template();
    this.messagesEl = this.container.querySelector<HTMLElement>(".messages")!;
    this.inputEl = this.container.querySelector<HTMLInputElement>(".chat-input")!;
    this.sendBtn = this.container.querySelector<HTMLButtonElement>(".send-btn")!;
    this.chipsRowEl = this.container.querySelector<HTMLElement>(".chips-row")!;
    this.sendBtn.addEventListener("click", () => this._send());
    this.inputEl.addEventListener("keydown", (e) => {
      if (e.key === "Enter") this._send();
    });
  }

  restoreHistory(turns: ConversationTurn[]): void {
    for (const turn of turns) {
      if (turn.role === "user") {
        this.appendUserMessage(turn.content, turn.chips_selected);
      } else {
        this.startAiMessage();
        if (this.currentAiBubble) {
          this.currentAiBubble.textContent = turn.content;
          this.currentAiBubble = null;
        }
      }
    }
  }

  setChips(chips: string[]): void {
    this.selectedChips.clear();
    this.chipsRowEl.innerHTML = chips
      .map(
        (c) => `<button class="chip" data-chip="${c}" type="button">${c}</button>`,
      )
      .join("");
    this.chipsRowEl.querySelectorAll<HTMLButtonElement>(".chip").forEach((btn) => {
      btn.addEventListener("click", () => {
        btn.classList.toggle("selected");
        const chip = btn.dataset["chip"]!;
        if (btn.classList.contains("selected")) {
          this.selectedChips.add(chip);
        } else {
          this.selectedChips.delete(chip);
        }
      });
    });
  }

  appendUserMessage(text: string, chips: string[]): void {
    const parts = [...chips.map((c) => `[${c}]`), text].filter(Boolean).join(" ");
    this.messagesEl.insertAdjacentHTML(
      "beforeend",
      `<div class="msg user">
        <div class="msg-avatar user" aria-hidden="true">You</div>
        <div class="msg-bubble">${this._escape(parts)}</div>
      </div>`,
    );
    this._scrollToBottom();
  }

  startAiMessage(): void {
    const wrapper = document.createElement("div");
    wrapper.className = "msg";
    wrapper.setAttribute("role", "status");
    wrapper.setAttribute("aria-live", "polite");
    wrapper.innerHTML = `<div class="msg-avatar ai" aria-hidden="true">AI</div><div class="msg-bubble"><div class="typing-indicator" aria-label="AI is typing"><span></span><span></span><span></span></div></div>`;
    this.messagesEl.appendChild(wrapper);
    this.currentAiBubble = wrapper.querySelector<HTMLElement>(".msg-bubble");
    this._scrollToBottom();
  }

  appendToken(token: string): void {
    if (this.currentAiBubble) {
      const indicator = this.currentAiBubble.querySelector(".typing-indicator");
      if (indicator) indicator.remove();
      this.currentAiBubble.textContent += token;
      this._scrollToBottom();
    }
  }

  showError(message: string): void {
    this.messagesEl.insertAdjacentHTML(
      "beforeend",
      `<div class="msg error-msg">
        <div class="msg-bubble" style="background:rgba(247,103,86,0.1);color:var(--swo-red);border-color:rgba(247,103,86,0.3);">
          ${this._escape(message)}
          <button class="retry-btn btn" style="margin-left:var(--space-sm);font-size:11px;padding:4px 10px;" type="button">Retry</button>
        </div>
      </div>`,
    );
    this.container.querySelector<HTMLButtonElement>(".retry-btn")?.addEventListener("click", () => {
      this._send();
    });
    this._scrollToBottom();
  }

  /** Fire an agent-initiated opening message with no user input. */
  kickOff(): void {
    this.setLoading(true);
    this.startAiMessage();
    this.cancelStream = createChatStream(
      this.opts.sessionId,
      "",
      [],
      (event: ChatEvent) => {
        if (event.type === "token") {
          this.appendToken(event.token);
        } else if (event.type === "blueprint_update") {
          this.opts.onBlueprintUpdate(event);
        } else if (event.type === "stage_change") {
          this.opts.onStageChange(event);
        } else if (event.type === "chip_suggestions") {
          this.setChips(event.chips);
        } else if (event.type === "error") {
          this.showError(event.message);
        }
      },
      () => {
        this.setLoading(false);
        this.currentAiBubble = null;
      },
      (err) => {
        this.showError(`Connection error: ${err}`);
        this.setLoading(false);
      },
    );
  }

  setLoading(loading: boolean): void {
    this.sendBtn.disabled = loading;
    this.inputEl.disabled = loading;
    this.sendBtn.innerHTML = loading
      ? `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true" style="animation:spin 1s linear infinite"><circle cx="12" cy="12" r="10" stroke-dasharray="40" stroke-dashoffset="10"/></svg>`
      : SEND_ICON;
  }

  unmount(): void {
    this.cancelStream?.();
  }

  private _send(): void {
    const message = this.inputEl.value.trim();
    const chips = [...this.selectedChips];

    if (!message && chips.length === 0) {
      this.inputEl.placeholder = "Please select an option or type a response";
      return;
    }

    this.appendUserMessage(message, chips);
    this.inputEl.value = "";
    this.chipsRowEl.innerHTML = "";
    this.selectedChips.clear();
    this.setLoading(true);
    this.startAiMessage();

    this.cancelStream = createChatStream(
      this.opts.sessionId,
      message,
      chips,
      (event: ChatEvent) => {
        if (event.type === "token") {
          this.appendToken(event.token);
        } else if (event.type === "blueprint_update") {
          this.opts.onBlueprintUpdate(event);
        } else if (event.type === "stage_change") {
          this.opts.onStageChange(event);
        } else if (event.type === "chip_suggestions") {
          this.setChips(event.chips);
        } else if (event.type === "error") {
          this.showError(event.message);
        }
      },
      () => {
        this.setLoading(false);
        this.currentAiBubble = null;
      },
      (err) => {
        this.showError(`Connection error: ${err}`);
        this.setLoading(false);
      },
    );
  }

  private _scrollToBottom(): void {
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
  }

  private _escape(text: string): string {
    return text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  }

  private _template(): string {
    return `
      <div class="pane-label">
        <span>Conversation</span>
      </div>
      <div class="messages" role="log" aria-label="Conversation messages" aria-live="polite"></div>
      <div class="chips-row" role="group" aria-label="Suggested responses"></div>
      <div class="input-area">
        <div class="input-box">
          <input
            type="text"
            class="chat-input"
            placeholder="Or type your own answer..."
            aria-label="Your message"
          />
          <button class="send-btn" type="button" aria-label="Send message">${SEND_ICON}</button>
        </div>
        <div class="input-hint">Your answers are processed securely in SoftwareOne's hosted environment.</div>
      </div>
    `;
  }
}
